import React from "react";

import {
  DefaultErrorFallback,
  type ErrorBoundaryFallbackProps,
} from "@/components/errors/DefaultErrorFallback";

export { DefaultErrorFallback, type ErrorBoundaryFallbackProps };

export interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ReactNode | ((args: ErrorBoundaryFallbackProps) => React.ReactNode);
  onError?: (error: Error, info: React.ErrorInfo) => void;
  onReset?: () => void;
  resetKeys?: unknown[];
}

interface ErrorBoundaryState { hasError: boolean; error?: Error | null }

export default class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    if (typeof this.props.onError === "function") {
      this.props.onError(error, info);
    }
  }

  componentDidUpdate(prevProps: Readonly<ErrorBoundaryProps>): void {
    if (this.state.hasError && this.shouldReset(prevProps.resetKeys, this.props.resetKeys)) {
      this.reset();
    }
  }

  private shouldReset(prev?: unknown[], next?: unknown[]) {
    if (!prev && !next) return false;
    if ((!prev && next) || (prev && !next)) return true;
    if (!prev || !next) return false;
    if (prev.length !== next.length) return true;
    for (let i = 0; i < prev.length; i++) {
      if (prev[i] !== next[i]) return true;
    }
    return false;
  }

  private reset = () => {
    if (typeof this.props.onReset === "function") this.props.onReset();
    this.setState({ hasError: false, error: null });
  };

  render(): React.ReactNode {
    if (this.state.hasError) {
      const { fallback } = this.props;
      if (typeof fallback === "function") {
        return fallback({ error: this.state.error, reset: this.reset });
      }
      if (fallback) return fallback;
      return (
        <DefaultErrorFallback
          error={this.state.error ?? undefined}
          reset={() => window.location.reload()}
        />
      );
    }
    return this.props.children;
  }
}

